import { Badge, Theme } from "@120wateraudit/waterworks";
import React from "react";
import { Popup } from "semantic-ui-react";
import { SubmissionStatus } from "src/types/Submission";
import styled from "styled-components";

export type Status = keyof typeof SubmissionStatus;

const STATUS_PALETTE_MAP: Record<Status, { background: string; text: string }> = {
  approved: {
    background: Theme.palette.badgeSuccessBackground,
    text: Theme.palette.badgeSuccessText,
  },
  inReview: {
    background: Theme.palette.badgeInProgressBackground,
    text: Theme.palette.badgeInProgressText,
  },
  notSubmitted: {
    background: Theme.palette.badgeInactiveBackground,
    text: Theme.palette.badgeInactiveText,
  },
  rejected: {
    background: Theme.palette.badgeErrorBackground,
    text: Theme.palette.badgeErrorText,
  },
  submitted: {
    background: Theme.palette.badgePendingBackground,
    text: Theme.palette.badgePendingText,
  },
  processingSubmission: {
    background: Theme.palette.badgePendingBackground,
    text: Theme.palette.badgePendingText,
  },
  voided: {
    background: Theme.palette.badgeInProgressBackground,
    text: Theme.palette.badgeInProgressText,
  },
  failed: {
    background: Theme.palette.badgeErrorBackground,
    text: Theme.palette.badgeErrorText,
  },
};

export const getBackgroundFromStatus = (status: Status): string =>
  STATUS_PALETTE_MAP[status]?.background ?? Theme.palette.badgeInactiveBackground;

export const getTextFromStatus = (status: Status): string =>
  STATUS_PALETTE_MAP[status]?.text ?? Theme.palette.badgeInactiveText;

// Backwards compatibility: original name returned background color
export const getColorFromStatus = getBackgroundFromStatus;

interface StatusBadgeProps {
  status: Status;
  style?: React.CSSProperties;
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, style }) => {
  const statusLabel = SubmissionStatus[status];

  if (status === "failed") {
    return (
      <StyledPopup
        trigger={
          <div>
            <Badge
              style={{
                ...style,
                justifyContent: "center",
                textAlign: "center",
                minWidth: "120px",
                color: getTextFromStatus(status),
              }}
              backgroundColor={getBackgroundFromStatus(status)}
            >
              {statusLabel}
            </Badge>
          </div>
        }
      >
        <PopupText>
          {
            "There was an error processing this submission. Utility must submit again."
          }
        </PopupText>
      </StyledPopup>
    );
  }

  return (
    <Badge
      style={{
        ...style,
        justifyContent: "center",
        textAlign: "center",
        minWidth: "120px",
        color: getTextFromStatus(status),
      }}
      backgroundColor={getBackgroundFromStatus(status)}
    >
      {statusLabel}
    </Badge>
  );
};

export const StyledPopup = styled(Popup)`
  &&& {
    background-color: #192a3e;
    border-radius: 5px;
    :before {
      background-color: #192a3e;
    }
  }
`;

const PopupText = styled.div`
  background-color: #192a3e;
  color: #ffffff;
  white-space: pre-line;
`;
